import { Injectable } from '@angular/core';
import { CARD_PURCHASE_STATUS, ModelCardRenderedSelection } from 'src/app/models/card';
import { ICardCollection } from 'src/app/models/collection';
import { sortCardsByDate } from '../pipes/sort-by-date.pipe';
import { CardsService } from './cards.service';

@Injectable({
  providedIn: 'root'
})
export class ReviewCollectionsService {
  constructor(private readonly cardsService: CardsService) {}

  public async getReviewsCollectionByStatus(reviewStatus: string): Promise<ICardCollection> {
    const cards = await this.cardsService.getReviewsCardsByStatus(reviewStatus);
    return this.buildReviewsVirtualCollection(reviewStatus, cards, { sortMode: 'reviewCreatedAt' });
  }

  public async getPublicStoreCollection(): Promise<ICardCollection> {
    const cards = await this.cardsService.getPublicStorePublishedCards();
    return this.buildReviewsVirtualCollection(CARD_PURCHASE_STATUS.PUBLISHED, cards, { sortMode: 'storeModifiedDate' });
  }

  private buildReviewsVirtualCollection(
    reviewStatus: string,
    cards: ModelCardRenderedSelection[],
    options: { sortMode?: 'reviewCreatedAt' | 'storeModifiedDate' } = {}
  ): ICardCollection {
    const { sortMode = 'reviewCreatedAt' } = options;
    const nowIso = new Date().toISOString();
    const collectionTitle = reviewStatus === CARD_PURCHASE_STATUS.PUBLISHED || reviewStatus === 'published'
      ? 'Published'
      : 'In Review';
    const sortedCards = sortMode === 'storeModifiedDate'
      ? this.sortStoreCardsByModifiedDate(cards)
      : sortCardsByDate([...(cards || [])], 'reviewCreatedAt');

    return {
      collectionId: `reviews-${reviewStatus}`,
      collectionSharingId: '',
      collectionTitle,
      collectionContentMobile: sortedCards[0]?.cardContentMobile || '',
      collectionPosterMobile: sortedCards[0]?.cardPosterMobile || '',
      collectionPosterWeb: sortedCards[0]?.cardPosterWeb || '',
      collectionContentWeb: sortedCards[0]?.cardContentWeb || '',
      collectionAuthor: '',
      collectionDescription: '',
      collectionCards: sortedCards,
      purchaseDetails: {
        allCardsQuantity: sortedCards.length,
      },
      created: nowIso,
      updated: nowIso,
    };
  }

  private sortStoreCardsByModifiedDate(cards: ModelCardRenderedSelection[]): ModelCardRenderedSelection[] {
    return [...(cards || [])].sort((cardA, cardB) => {
      const cardATimestamp = this.resolveStoreSortTimestamp(cardA);
      const cardBTimestamp = this.resolveStoreSortTimestamp(cardB);
      return cardBTimestamp - cardATimestamp;
    });
  }

  private resolveStoreSortTimestamp(card: ModelCardRenderedSelection): number {
    const cardRecord = card as unknown as Record<string, unknown>;
    const reviewCreatedAt =
      typeof cardRecord?.['reviewCreatedAt'] === 'string'
        ? (cardRecord['reviewCreatedAt'] as string)
        : '';

    return this.resolveIsoDateTimestamp(card?.modifiedDate) || this.resolveIsoDateTimestamp(reviewCreatedAt);
  }

  private resolveIsoDateTimestamp(isoDate?: string): number {
    if (!isoDate) return 0;
    const timestamp = new Date(isoDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
}
